import { Button, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createUnauthenticatedPostgrest } from "../../utils/unauthenticatedApi";
import { AIRPLX_TOKEN } from "../../containers/IdentityContainer";
import { Loading } from "../../widgets/Loading";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const ResetPassword: React.FC<{}> = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = React.useState<string>("");
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  // Create unauthenticated postgrest client for public endpoint
  const postgrest = React.useMemo(() => {
    return createUnauthenticatedPostgrest();
  }, []);

  const validatePassword = (): boolean => {
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError("Password must contain at least one uppercase letter");
      return false;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError("Password must contain at least one number");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleResetPassword = async () => {
    setError("");

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const { data: token } = await postgrest.rpc("reset_password_with_code", {
        p_code: code,
        p_new_password: newPassword,
      });

      if (!token || token.length === 0) {
        setError("Invalid or expired reset link. Please request a new one.");
        setLoading(false);
        return;
      }

      // Store token and reload (auto-login)
      // Use reload instead of navigate to ensure React state reinitializes
      localStorage.setItem(AIRPLX_TOKEN, token[0]);
      window.location.reload();
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
      console.error("Error resetting password:", err);
      setLoading(false);
    }
  };

  const getPasswordStrength = (): string => {
    if (!newPassword) return "";
    if (newPassword.length < 8) return "Too short";

    let strength = 0;
    if (/[A-Z]/.test(newPassword)) strength++;
    if (/[a-z]/.test(newPassword)) strength++;
    if (/[0-9]/.test(newPassword)) strength++;
    if (/[^A-Za-z0-9]/.test(newPassword)) strength++;

    if (strength === 4 && newPassword.length >= 12) return "Strong";
    if (strength >= 3) return "Good";
    if (strength >= 2) return "Fair";
    return "Weak";
  };

  const passwordStrength = getPasswordStrength();

  if (!code) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
        width="100%"
      >
        <Typography variant="h5">Invalid reset link</Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/login")}
          sx={{ mt: 2 }}
        >
          Back to login
        </Button>
      </Stack>
    );
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        width="100vw"
      >
        <Stack direction="column" width={450} spacing={3}>
          <Stack spacing={1} alignItems="center">
            <Typography variant="h5">Reset Your Password</Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              Choose a strong password
            </Typography>
          </Stack>

          <TextField
            fullWidth
            label="New Password"
            variant="outlined"
            type="password"
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleResetPassword()}
            value={newPassword}
            autoFocus
          />

          {newPassword && (
            <Typography
              variant="body2"
              sx={{ mt: -1 }}
              color={
                passwordStrength === "Strong" ? "success.main" :
                passwordStrength === "Good" ? "primary.main" :
                passwordStrength === "Fair" ? "warning.main" :
                "error.main"
              }
            >
              Strength: {passwordStrength}
            </Typography>
          )}

          <TextField
            fullWidth
            label="Confirm Password"
            variant="outlined"
            type="password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleResetPassword()}
            value={confirmPassword}
            error={Boolean(error)}
            helperText={error}
            sx={{ mt: newPassword ? 0 : 2 }}
          />

          <Typography variant="caption" color="text.secondary" align="center">
            Must be 8+ characters with uppercase, lowercase, and numbers
          </Typography>

          <Button
            disabled={!newPassword || !confirmPassword || loading}
            variant="contained"
            color="primary"
            onClick={handleResetPassword}
            fullWidth
            sx={{ borderRadius: 1 }}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>

          <Button
            size="small"
            onClick={() => navigate("/login")}
          >
            Back to login
          </Button>
        </Stack>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
